/******************************** Import Package ************************************/
import React, { useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  CssBaseline,
  Divider,
  ThemeProvider,
  IconButton,
  createTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";

/******************************* Import Pages ************************************/
import Dashboard from "../Pages/Dashboard";
import Unauthorized from "../Pages/Unauthorized";
import Login from "../Pages/Login";
import TimeOut from "../Pages/TimeOut";
import GoogleAuth from "../Pages/GoogleAuth";
/******************************* Import Components *********************************/
import SideNavigate from "./SideNavigate";
import Header from "./Header";
import Loader from "../Components/Loader";
import Notification from "../Components/Notification";

/******************************* Import Constants **********************************/
import * as menuData from "../Constants/menuData";
import * as dashBoardData from "../Constants/dashboardData";
import * as accessLevel from "../Constants/accessLevel";
import { env, hostConfig, idleTime } from "../Config";
/******************************* Import Styles **********************************/
import { useAppStyles } from "./App.Style";
import "./App.css";

/******************************* Import Pages lazy load************************************/
const Pillars = React.lazy(() => import("../Pages/Admin/Pillars"));
const Projects = React.lazy(() => import("../Pages/Admin/Projects"));
const Goals = React.lazy(() => import("../Pages/Admin/Goals"));
const Users = React.lazy(() => import("../Pages/Admin/Users"));
const RequestHistory = React.lazy(() =>
  import("../Pages/Admin/RequestHistory")
);

const PendingApproval = React.lazy(() =>
  import("../Pages/Admin/PendingApproval")
);
const PayRelease = React.lazy(() => import("../Pages/Admin/Reports"));
const ProposedProjects = React.lazy(() =>
  import("../Pages/Admin/ProposedProjects")
);
const EditLocation = React.lazy(() => import("../Pages/Admin/Location"));
const NotFound = React.lazy(() => import("../Pages/NotFound"));
const LoggedInHistory = React.lazy(() =>
  import("../Pages/Admin/LoggedInHistory")
);

/**********************************Theme color****************************************/

const theme = createTheme({
  palette: {
    primary: {
      light: "#9acad6",
      main: "#4e9a98",
      dark: "#87bcb4",
      contrastText: "#fff",
    },
    secondary: {
      main: "#f50057", // 🌟 your global secondary color
      contrastText: "#fff", // button text color
    },
  },
});

/***********************************App****************************************/

export default function App() {
  const {
    Root,
    AppBarStyled,
    MenuButton,
    DrawerStyled,
    ToolbarStyled,
    SideMenu,
    SideMenuItem,
    CustomWidth,
    Content,
  } = useAppStyles();

  const [open, setOpen] = useState(false);

  let roleData;
  let menuLinks;
  let dashBoardLinks;
  // let role;
  let userData = {};

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  /***********************************Find  User Role****************************************/
  if (JSON.parse(localStorage.getItem("loggedUser"))) {
    userData = JSON.parse(localStorage.getItem("loggedUser"));
    if (window.location.pathname === "/") {
      window.location.href = "/dashboard";
    }
  } else {
    const location = window.location.pathname;
    if (location !== "/login") {
      if (location === "/google/auth" || location === "/unauthorized") {
      } else {
        if (env === 4 || env === 2) {
          window.location.href = `${hostConfig.API_URL}auth/google`;
        } else {
          window.location.href = "/login";
        }
      }
    }
  }

  /***********************************Inactive Time****************************************/
  const inactivityTime = () => {
    let time;
    const logout = () => {
      window.location.href = "/time-out";
    };
    const resetTimer = () => {
      clearTimeout(time);
      time = setTimeout(logout, idleTime);
    };
    window.onload = resetTimer;
    window.addEventListener("load", resetTimer, true);
    [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "touchmove",
      "wheel",
      "select",
    ].forEach((name) => {
      document.addEventListener(name, resetTimer, true);
    });
  };

  window.onload = () => {
    inactivityTime();
  };

  /***********************************Response Handler****************************************/
  const responseHandler = (res) => {
    if (res) {
      if (res.code) {
        if (res.code === 400) {
          setNotify({
            isOpen: true,
            message: res.message,
            type: "error",
          });
          return false;
        } else {
          setNotify({
            isOpen: true,
            message: "Bad Request",
            type: "error",
          });
          return false;
        }
      } else if (res.error) {
        setNotify({
          isOpen: true,
          message: res.error,
          type: "error",
        });
        return false;
      } else {
        return res;
      }
    } else {
      return false;
    }
  };

  /***********************************Role Based Data****************************************/
  let currentUser = localStorage.getItem("currentUser");

  if (currentUser === "admin") {
    roleData = accessLevel.adminRights;
    menuLinks = menuData.adminMenu;
    dashBoardLinks = dashBoardData.adminDashBoard;
    // role = "admin";
  } else if (currentUser === "approver") {
    roleData = accessLevel.approverRights;
    menuLinks = menuData.approverMenu;
    dashBoardLinks = dashBoardData.approverDashBoard;
    // role = "approver";
  } else if (currentUser === "sponsor") {
    roleData = accessLevel.sponsorRights;
    menuLinks = menuData.sponsorMenu;
    dashBoardLinks = dashBoardData.sponsorDashBoard;
    // role = "sponsor";
  } else {
    roleData = accessLevel.resourceRights;
    menuLinks = menuData.resourceMenu;
    dashBoardLinks = dashBoardData.resourceDashBoard;
    // role = "resource";
  }

  /***********************************Drawer Handlers****************************************/
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  /***********************************Return App****************************************/
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Root className="App">
          <CssBaseline />
          {userData?.email ? (
            <AppBarStyled
              position="fixed"
              style={{ paddingRight: "0px" }}
              open={open}
            >
              <CustomWidth>
                <MenuButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="center"
                  open={open}
                >
                  <MenuIcon style={{ width: "56px" }} />
                </MenuButton>
                <Header responseHandler={responseHandler} />
              </CustomWidth>
            </AppBarStyled>
          ) : null}

          {userData?.email ? (
            <DrawerStyled
              id="navigation-drawer"
              variant="permanent"
              open={open}
            >
              <ToolbarStyled>
                <IconButton
                  onClick={handleDrawerClose}
                  aria-label="Close drawer"
                >
                  {theme.direction === "rtl" ? (
                    <ChevronRight />
                  ) : (
                    <ChevronLeft />
                  )}
                </IconButton>
              </ToolbarStyled>
              <SideMenu>
                {menuLinks.map((item) => (
                  <SideMenuItem key={item.id}>
                    <SideNavigate
                      name={item.name}
                      link={item.link}
                      icon={item.icon}
                      open={open}
                    />
                  </SideMenuItem>
                ))}
              </SideMenu>
              <Divider />
            </DrawerStyled>
          ) : null}

          <Content open={open} id="main">
            <Suspense fallback={<Loader />}>
              <Routes>
                {/*************************** * General Routes *********************************************/}
                <Route
                  path="/login"
                  element={<Login responseHandler={responseHandler} />}
                />
                <Route path="/google/auth" element={<GoogleAuth />} />
                <Route
                  path="/dashboard"
                  element={
                    <Dashboard
                      dashBoardLinks={dashBoardLinks}
                      dashBoardAccess={roleData.dashboard}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/unauthorized"
                  element={
                    <Unauthorized
                      dashBoardLinks={dashBoardLinks}
                      dashBoardAccess={roleData.dashboard}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route path="/404" element={<NotFound />} />
                <Route path="/time-out" element={<TimeOut />} />

                {/*************************** * Admin Routes *********************************************/}
                <Route
                  path="/pillars"
                  element={
                    <Pillars
                      pillarAccess={roleData.pillars}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/goals"
                  element={
                    <Goals
                      goalAccess={roleData.goals}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/users"
                  element={
                    <Users
                      userAccess={roleData.users}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <Projects
                      projectAccess={roleData.projects}
                      serviceAccess={roleData.services}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/request"
                  element={
                    <RequestHistory
                      serviceAccess={roleData.services}
                      paymentAccess={roleData.payments}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/pending-approval"
                  element={
                    <PendingApproval
                      serviceAccess={roleData.services}
                      paymentAccess={roleData.payments}
                      pendingRequestAccess={roleData.pendingRequestAccess}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/proposed-projects"
                  element={
                    <ProposedProjects
                      paymentAccess={roleData.payments}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <PayRelease
                      payReleaseAccess={roleData.payRelease}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/location"
                  element={
                    <EditLocation
                      locationAccess={roleData.location}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/log-in-history"
                  element={
                    <LoggedInHistory
                      userAccess={roleData.users}
                      responseHandler={responseHandler}
                    />
                  }
                />

                {/* Catch all → Redirect to 404 */}
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
            <Notification notify={notify} setNotify={setNotify} />
          </Content>
        </Root>
      </Router>
    </ThemeProvider>
  );
}
